import React from 'react';
import { useContest } from '../Context/ContestContext';
import { useGames } from '../Context/GameContext';
import { getMediaUrl } from '../Services/apiService';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, ArrowRight } from 'lucide-react';
import { Card, Row, Col, Button, Badge } from 'react-bootstrap';

const ActiveContestsList = () => {
    const { activeGames, rejoinAsHost } = useContest();
    const { games } = useGames();

    if (!activeGames || activeGames.length === 0) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mb-5"
            >
                <div className="d-flex align-items-center gap-2 mb-3">
                    <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                        className="rounded-circle bg-danger p-1"
                        style={{ width: 10, height: 10 }}
                    />
                    <h4 className="fw-bold text-dark mb-0">Devam Eden Aktif Yarışmalar</h4>
                    <Badge bg="danger" className="ms-2 px-2 py-1 rounded-pill animate-pulse">CANLI</Badge>
                </div>

                <Row className="g-4">
                    {activeGames.map((activeGame) => {
                        // Find matching game template in database
                        const dbGame = games.find(g => 
                            g.id === activeGame.gameId || 
                            g.title === activeGame.title ||
                            (g.GameTitle && g.GameTitle === activeGame.title)
                        );
                        
                        const coverImage = dbGame?.image;
                        const questionCount = dbGame?.questions?.length || activeGame.totalQuestions || 0;

                        return (
                            <Col xs={12} key={activeGame.pin}>
                                <Card 
                                    className="border-0 shadow-sm overflow-hidden position-relative"
                                    style={{
                                        background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
                                        borderRadius: '16px'
                                    }}
                                >
                                    {/* Futuristic background light effect */}
                                    <div 
                                        className="position-absolute top-0 end-0 opacity-20 z-0 pointer-events-none"
                                        style={{
                                            width: '300px',
                                            height: '100%',
                                            background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)'
                                        }}
                                    />

                                    <Card.Body className="p-4 position-relative z-1 d-flex flex-column flex-md-row align-items-center justify-content-between gap-4">
                                        <div className="d-flex align-items-center gap-4 flex-wrap flex-md-nowrap w-100">
                                            {/* Game Image */}
                                            {coverImage ? (
                                                <div 
                                                    className="rounded-3 overflow-hidden shadow-sm flex-shrink-0 border border-secondary border-opacity-20" 
                                                    style={{ width: 120, height: 80 }}
                                                >
                                                    <img 
                                                        src={getMediaUrl(coverImage)} 
                                                        alt="Cover" 
                                                        className="w-100 h-100 object-fit-cover" 
                                                    />
                                                </div>
                                            ) : (
                                                <div 
                                                    className="rounded-3 bg-secondary bg-opacity-20 d-flex align-items-center justify-content-center flex-shrink-0 border border-secondary border-opacity-20" 
                                                    style={{ width: 120, height: 80 }}
                                                >
                                                    <span className="text-secondary small">Kapak Yok</span>
                                                </div>
                                            )}

                                            {/* Game Info */}
                                            <div className="text-start flex-grow-1 min-width-0">
                                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                                    <h5 className="fw-bold text-white mb-0 text-truncate">{activeGame.title}</h5>
                                                    <Badge bg="success" className="bg-opacity-10 text-success border border-success border-opacity-25 px-2 py-1 font-monospace">
                                                        PIN: {activeGame.pin}
                                                    </Badge>
                                                </div>
                                                <p className="text-white opacity-50 small mb-0 d-flex align-items-center gap-3">
                                                    <span>Oluşturan: <strong className="text-white">{activeGame.creatorName || 'Moderatör'}</strong></span>
                                                    <span>•</span>
                                                    <span>{questionCount} Soru</span>
                                                </p>
                                            </div>

                                            {/* Live Stats */}
                                            <div className="d-flex align-items-center gap-4 flex-shrink-0">
                                                <div className="text-center">
                                                    <small className="text-secondary d-block fw-bold ls-1 mb-1" style={{ fontSize: '10px' }}>OYUNCU</small>
                                                    <Badge bg="primary" className="px-3 py-2 fs-6 rounded-pill d-flex align-items-center gap-2">
                                                        <Users size={16} /> {activeGame.playerCount || 0}
                                                    </Badge>
                                                </div>

                                                <div className="text-center">
                                                    <small className="text-secondary d-block fw-bold ls-1 mb-1" style={{ fontSize: '10px' }}>DURUM</small>
                                                    <Badge 
                                                        bg={activeGame.status === 'LOBBY' ? 'warning' : 'danger'} 
                                                        className="px-3 py-2 fs-6 rounded-pill text-uppercase"
                                                    >
                                                        {activeGame.status === 'LOBBY' ? 'LOBİDE' : 'OYNANIYOR'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Button */}
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            onClick={() => rejoinAsHost(activeGame.pin, dbGame?.id)}
                                            className="px-4 py-3 fw-bold rounded-pill shadow-lg border-0 d-flex align-items-center justify-content-center gap-2 flex-shrink-0"
                                            style={{
                                                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                                minWidth: '180px'
                                            }}
                                        >
                                            <span>Yarışmayı Yönet</span>
                                            <ArrowRight size={18} />
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            </motion.div>
        </AnimatePresence>
    );
};

export default ActiveContestsList;
